/* -------------------------------------------------------------------------- */
/*                            External Dependencies                           */
/* -------------------------------------------------------------------------- */
import styled, { css } from 'styled-components';

/* --------------------------- Internal Dependency -------------------------- */
import { respondTo } from './breakpoint';

export type variant = 'paymentSuccess' | 'transferConfirmation';

const BackgroundWrapper = styled.div`
    background: #090719;
    color: var(--white);
    -webkit-font-smoothing: subpixel-antialiased;
    backface-visibility: hidden;
    width: 100%;
    height: 100%;

    overflow-x: hidden;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
`;

const AvoidFlickering = styled.div`
    visibility: hidden;
`;

const Yat = styled.div`
    position: absolute;
    font-size: 9vmin;
    top: -3%;
    transform-origin: 0 0;
    user-select: none;
    ${respondTo(
        'lg',
        css`
            font-size: 6vmin;
        `,
    )};
`;

const YatBackdrop = styled.div`
    position: absolute;
    height: 100%;
    width: 100%;
    left: 0;
    top: 0;
    z-index: 1;

    ${respondTo(
        'md',
        css`
            overflow: hidden;
            z-index: 0;
        `,
    )}
`;

const YatGeneration = styled.div`
    text-align: center;
    padding: 0 0 60px 0;
`;

const YatLanding = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ConfirmationText = styled.div<Props>`
    ${(props) =>
        props.$variant === 'transferConfirmation' &&
        css`
            position: relative;
            display: flex;
            top: unset;
            bottom: unset;
            transform: unset;
        `}

    .text-wrap {
        margin: 0;
        min-height: 155px;
        width: 100%;
        display: inline-block;
        overflow: hidden;

        ${respondTo(
            'md',
            css`
                width: 100vw;
                height: 25vmin;
                min-height: unset;
            `,
        )}

        ${respondTo(
            'xs',
            css<Props>`
                width: 100vw;
                height: 20vmin;
                ${(props) =>
                    props.$variant === 'transferConfirmation' &&
                    css`
                        height: 25vmin;
                    `}
            `,
        )}
    }

    h1 {
        font-style: normal;
        font-weight: 900;
        font-size: 192px;
        line-height: 0.8;
        white-space: nowrap;
        margin: 0;
        text-align: center;
        letter-spacing: -0.08em;
        text-transform: uppercase;
        color: #211f30;
        opacity: 1;

        ${respondTo(
            'md',
            css`
                font-size: 22vmin;
            `,
        )}
    }

    .compact {
        h1 {
            font-size: 120px;
            line-height: 0.8;
            letter-spacing: -7%;
            color: #fff;
            ${respondTo(
                'lg',
                css`
                    font-size: 80px;
                `,
            )}
            ${respondTo(
                'sm',
                css`
                    font-size: 52px;
                `,
            )}
        }
    }

    ${respondTo(
        'xs',
        css`
            top: 35%;
        `,
    )}
`;

interface Props {
    $variant: variant;
}

const ActionSlate = styled.div<Props>`
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 2;
    ${(props) =>
        props.$variant === 'transferConfirmation' &&
        css`
            position: relative;
            display: flex;
            top: unset;
            bottom: unset;
            transform: unset;
        `}

    ${respondTo(
        'md',
        css`
            bottom: 10%;
        `,
    )}

    h4 {
        font-style: normal;
        font-weight: bold;
        font-size: 24px;
        line-height: 1.5;
        text-align: center;
        margin-bottom: 1.5rem;
        color: rgba(255, 255, 255, 0.3);
        white-space: pre;
        ${respondTo(
            'md',
            css`
                font-size: 20px;
                line-height: 0.92;
            `,
        )}

        ${respondTo(
            'sm',
            css`
                white-space: normal;
                max-width: calc(100% - 40px);
            `,
        )}
    }
`;

const YatEmoji = styled.div<Props>`
    background: linear-gradient(
            225deg,
            rgba(255, 255, 255, 0) 31.25%,
            rgba(255, 255, 255, 0.5) 46.87%,
            rgba(255, 255, 255, 0) 62.5%
        ),
        linear-gradient(
            135deg,
            rgba(255, 255, 255, 0) 32.81%,
            rgba(255, 255, 255, 0.5) 48.44%,
            rgba(255, 255, 255, 0) 64.06%
        ),
        linear-gradient(0deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1)), #090719;
    display: block;
    padding: 1.313rem 0.063rem 1.313rem 1.313rem;
    position: absolute;
    border-radius: 80px;
    z-index: 1;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%) rotate(-15deg);
    ${respondTo(
        'md',
        css<Props>`
            transform: translate(-50%, -50%) rotate(-15deg) scale(1) !important;
            ${(props) =>
                props.$variant === 'transferConfirmation' &&
                css`
                    transform: none !important;
                `}
        `,
    )}
    ${(props) =>
        props.$variant === 'transferConfirmation' &&
        css`
            position: relative;
            display: flex;
            top: unset;
            bottom: unset;
            transform: none;
            left: unset;
            margin-bottom: 40px;
        `}

    .slate-backdrop {
        box-shadow: inset 0 -3px 7px rgb(231 2 85 / 50%);
        filter: drop-shadow(0 0 40px rgba(231, 2, 85, 0.6));
        background: linear-gradient(224.27deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 72.49%), #1c1a29;
        border-radius: 80px;
        height: 100%;
        width: 100%;
        position: absolute;
        left: 50%;
        z-index: 2;
        top: 50%;
        transform: translate(-50%, -48%);
    }

    h3 {
        font-style: normal;
        font-size: 65px;
        z-index: 2;
        position: relative;
        text-align: center;
        margin: 0;
        letter-spacing: 20px;

        ${respondTo(
            'md',
            css`
                font-size: 10vmin;
                white-space: pre;
                letter-spacing: 10px;
            `,
        )}
    }
    ${respondTo(
        'xs',
        css<Props>`
            top: 47.5%;
            ${(props) =>
                props.$variant === 'transferConfirmation' &&
                css`
                    top: unset;
                    margin-bottom: 20px;
                    margin-top: 20px;
                `}
        `,
    )}
`;

const Button = styled.button`
    background: var(--theme-primary);
    box-shadow: 0 0 40px rgba(82, 61, 241, 0.5);
    border-radius: 40px;
    font-style: normal;
    font-weight: bold;
    font-size: 16px;
    line-height: 82%;

    margin: 0 auto;
    text-align: center;
    padding: 1.75rem 4.063rem;
    border-radius: 50px;
    border: none;
    cursor: pointer;
    color: var(--white);
`;

const ContentHolder = styled.div`
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    position: relative;
    transform: translate-origin(50%, 50%);
    min-height: 600px;
    padding: 0 20px;
`;

export {
    YatEmoji,
    ActionSlate,
    ConfirmationText,
    YatBackdrop,
    YatGeneration,
    YatLanding,
    Yat,
    AvoidFlickering,
    BackgroundWrapper,
    Button,
    ContentHolder,
};
